import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { useAuthContext } from './AuthContext';
import { isScopedAccessToken } from '../lib/auth/tokenClaims';

interface UserProfile {
    id: number;
    fullName: string;
    email: string;
    phoneNumber: string;
    countryCode: string;
    profileType: string;
}

interface UserProfileContextType {
    profile: UserProfile | null;
    loading: boolean;
    error: Error | null;
    loadProfile: () => Promise<void>;
    updateProfile: (payload: { fullName: string; email: string; phoneNumber: string; countryCode: string }) => Promise<boolean>;
    changePassword: (payload: { currentPassword: string; newPassword: string; confirmPassword: string }) => Promise<boolean>;
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);

export const UserProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);
    const { accessToken } = useAuthContext();

    const loadProfile = async () => {
        setLoading(true);
        try {
            const data = await apiService.getUserProfile();
            setProfile(data as UserProfile);
        } catch (err: unknown) {
            setError(err instanceof Error ? err : new Error('Unknown error'));
        } finally {
            setLoading(false);
        }
    };

    const updateProfile = async (payload: { fullName: string; email: string; phoneNumber: string; countryCode: string }) => {
        setLoading(true);
        try {
            const success = await apiService.updateUserProfile({
                fullName: payload.fullName,
                email: payload.email,
                phoneNumber: payload.phoneNumber,
                countryCode: payload.countryCode
            });
            if (success) {
                await loadProfile();
            }
            return success;
        } catch (err: unknown) {
            setError(err instanceof Error ? err : new Error('Unknown error'));
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const changePassword = async (payload: { currentPassword: string; newPassword: string; confirmPassword: string }) => {
        setLoading(true);
        try {
            const success = await apiService.changePassword(payload);
            return success;
        } catch (err: unknown) {
            setError(err instanceof Error ? err : new Error('Unknown error'));
            throw err;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!accessToken) {
            setProfile(null);
            setError(null);
            return;
        }
        // AB#1434.b — see UserContext note: skip `/user-profile` while the
        // token is identity-only so the chooser can complete without the
        // axios 401 interceptor wiping the session.
        if (!isScopedAccessToken(accessToken)) {
            setProfile(null);
            setError(null);
            return;
        }
        loadProfile();
    }, [accessToken]);

    return (
        <UserProfileContext.Provider value={{ profile, loading, error, loadProfile, updateProfile, changePassword }}>
            {children}
        </UserProfileContext.Provider>
    );
};

export const useUserProfileContext = () => {
    const ctx = useContext(UserProfileContext);
    if (!ctx) throw new Error('useUserProfileContext must be used within UserProfileProvider');
    return ctx;
};

export default UserProfileContext;
